import {type ReactNode, useCallback, useMemo, useState} from 'react'
import {
  type NativeSyntheticEvent,
  Pressable,
  ScrollView,
  StyleSheet,
  type TextLayoutEventData,
  View,
} from 'react-native'
import {useSafeAreaFrame} from 'react-native-safe-area-context'
import Svg, {Circle} from 'react-native-svg'
import {LinearGradient} from 'expo-linear-gradient'
import {
  type AppBskyFeedDefs,
  AppBskyFeedPost,
  AtUri,
  RichText as RichTextAPI,
} from '@atproto/api'
import {Trans, useLingui} from '@lingui/react/macro'
import {useNavigation} from '@react-navigation/native'

import {HITSLOP_20} from '#/lib/constants'
import {useOpenComposer} from '#/lib/hooks/useOpenComposer'
import {type NavigationProp} from '#/lib/routes/types'
import {sanitizeDisplayName} from '#/lib/strings/display-names'
import {sanitizeHandle} from '#/lib/strings/handles'
import {POST_TOMBSTONE, usePostShadow} from '#/state/cache/post-shadow'
import {UserAvatar} from '#/view/com/util/UserAvatar'
import {ThreadComposePrompt} from '#/screens/PostThread/components/ThreadComposePrompt'
import {atoms as a} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {GameController_Stroke2_Corner0_Rounded as GameController} from '#/components/icons/GameController'
import {Link} from '#/components/Link'
import {PostControls} from '#/components/PostControls'
import {RichText} from '#/components/RichText'
import {Text} from '#/components/Typography'
import * as bsky from '#/types/bsky'
import {type CapsuleModel} from '#/types/capsule'
import {SwipeUpLottie} from './SwipeUpLottie'

export type CapsuleOverlayMode = 'scroll' | 'playing' | 'ended'

export type CapsuleSkipStage = 'hidden' | 'countdown' | 'active'

/**
 * Capsule "scroll video" / "completing the video" front — same components as
 * the Taderok mobile immersive video feed:
 *
 *   scroll  — avatar + @handle, capsule description, actions (PostControls)
 *             and the "Write your reply" bar. User picks a capsule and taps
 *             the Play call-to-action.
 *   playing — after Play: the actions + reply bar are removed, the
 *             description is fully readable and the skip system (countdown
 *             → Skip) replaces the actions.
 *   ended   — video finished without the user pressing Play: the Play CTA
 *             morphs into a message that helps the user play the capsule or
 *             swipe up for the next one (auto-scroll after 5s).
 */
export function CapsuleOverlay({
  post,
  capsule,
  capsuleDescription,
  mode,
  feedContext,
  onPlayFromEnded,
  hintSeconds,
  canAutoAdvance,
  skipStage,
  skipCountdown,
  skipProgress,
  onSkip,
  onLikeCelebrated,
}: {
  post: AppBskyFeedDefs.PostView
  capsule: CapsuleModel | null
  capsuleDescription?: string
  mode: CapsuleOverlayMode
  feedContext?: string
  /** Play button inside the ended message (launches the capsule quiz). */
  onPlayFromEnded: () => void
  /** Remaining seconds until auto-scroll (null when no countdown is running). */
  hintSeconds: number | null
  canAutoAdvance: boolean
  skipStage: CapsuleSkipStage
  skipCountdown: number
  /** Normalized playback progress toward the skip threshold (0..1). */
  skipProgress: number
  onSkip: () => void
  /** A Like was added on this capsule. Tap position in page coordinates, for
      the flying-hearts celebration rendered by the screen shell. */
  onLikeCelebrated?: (x: number, y: number) => void
}) {
  const navigation = useNavigation<NavigationProp>()
  const {t: l} = useLingui()
  const {openComposer} = useOpenComposer()
  const postShadow = usePostShadow(post)

  const record = useMemo(() => {
    return bsky.dangerousIsType<AppBskyFeedPost.Record>(
      post.record,
      AppBskyFeedPost.isRecord,
    )
      ? post.record
      : undefined
  }, [post.record])
  const recordText = record?.text?.trim() ?? ''

  const title = capsule?.title?.trim()
  const rawDesc = capsuleDescription?.trim() || ''
  const bodyText = rawDesc || title || recordText || ''
  // Reuse record facets only when the body is exactly the post text.
  const facets = bodyText === recordText ? record?.facets : undefined
  const richText = useMemo(() => {
    return new RichTextAPI({text: bodyText, facets})
  }, [bodyText, facets])
  const hasText = bodyText.length > 0

  const displayName = sanitizeDisplayName(
    post.author.displayName || post.author.handle,
  )
  const handle = sanitizeHandle(post.author.handle, '@')

  const onPressReply = useCallback(() => {
    openComposer({
      replyTo: {
        uri: post.uri,
        cid: post.cid,
        text: record?.text || '',
        author: post.author,
        embed: post.embed,
        langs: record?.langs,
      },
      logContext: 'PostReply',
    })
  }, [openComposer, post, record])

  const rkey = useMemo(() => new AtUri(post.uri).rkey, [post.uri])

  // Strong scrim behind text + actions (scroll) or behind text (playing/ended).
  const gradientColors = useMemo(() => {
    if (mode === 'scroll' || hasText) {
      return [
        'rgba(0,0,0,0)',
        'rgba(0,0,0,0.62)',
        'rgba(0,0,0,0.93)',
        'rgba(0,0,0,0.93)',
      ] as const
    }
    // No text in the (removed) actions zone → keep the scrim light so the
    // video stays clearly visible under the skip control.
    return ['rgba(0,0,0,0)', 'rgba(0,0,0,0.3)', 'rgba(0,0,0,0.62)'] as const
  }, [mode, hasText])

  const description = hasText ? (
    <CapsuleDescription
      richText={richText}
      mode={mode}
      // In playing mode the skip system sits bottom-right; keep the text clear
      // of it. Skip is at the far bottom-right, so reserve that column.
      reserveRight={mode === 'playing'}
    />
  ) : null

  // Deleted post → nothing left to show over the video.
  if (postShadow === POST_TOMBSTONE) {
    return (
      <View
        style={[
          a.absolute,
          a.inset_0,
          a.z_20,
          a.align_center,
          a.justify_center,
          {backgroundColor: 'rgba(0, 0, 0, 0.8)'},
        ]}>
        <Text
          style={[
            a.text_2xl,
            a.font_bold,
            a.text_center,
            a.leading_tight,
            a.mx_xl,
          ]}>
          <Trans>Post has been deleted</Trans>
        </Text>
      </View>
    )
  }

  return (
    <View style={[a.absolute, a.inset_0, a.z_20]} pointerEvents="box-none">
      <View style={[a.flex_1]} pointerEvents="none" />
      <LinearGradient
        colors={gradientColors}
        locations={mode === 'scroll' || hasText ? [0, 0.45, 0.8, 1] : undefined}
        style={[a.w_full, a.pt_lg]}>
        <View style={[a.px_md]}>
          {/* Avatar + name + @handle — stays in every mode */}
          <View style={[a.w_full, a.flex_row, a.align_center, a.gap_md]}>
            <Link
              label={l`View ${displayName}'s profile`}
              to={{
                screen: 'Profile',
                params: {name: post.author.did},
              }}
              style={[a.flex_1, a.flex_row, a.gap_md, a.align_center]}>
              <UserAvatar type="user" avatar={post.author.avatar} size={34} />
              <View style={[a.flex_1]}>
                <Text
                  style={[a.text_md, a.font_bold, {color: 'white'}]}
                  emoji
                  numberOfLines={1}>
                  {displayName}
                </Text>
                <Text
                  style={[a.text_sm, {color: 'rgba(255,255,255,0.75)'}]}
                  numberOfLines={1}>
                  {handle}
                </Text>
              </View>
            </Link>
            {/* (No Follow button — for capsules the Play CTA replaces it.) */}
          </View>

          {description}

          {/* scroll mode — same actions + reply bar as the mobile feed */}
          {mode === 'scroll' && (
            <>
              {record && (
                <View style={[{marginTop: 6, marginLeft: -5}]}>
                  <PostControls
                    richText={richText}
                    post={postShadow}
                    record={record}
                    feedContext={feedContext}
                    logContext="ImmersiveVideo"
                    forceGoogleTranslate={true}
                    onPressReply={() =>
                      navigation.navigate('PostThread', {
                        name: post.author.did,
                        rkey,
                      })
                    }
                    big
                    onLike={onLikeCelebrated}
                  />
                </View>
              )}
              <ThreadComposePrompt
                onPressCompose={onPressReply}
                style={[a.pt_md, {paddingBottom: 14}]}
              />
            </>
          )}

          {/* ended mode — the Play CTA became a message */}
          {mode === 'ended' && (
            <EndedMessage
              onPlay={onPlayFromEnded}
              hintSeconds={hintSeconds}
              canAutoAdvance={canAutoAdvance}
            />
          )}

          {mode === 'playing' && <View style={{height: 40}} />}
        </View>
      </LinearGradient>

      {/* playing mode — skip system replaces the removed actions */}
      {mode === 'playing' && (
        <View
          style={[a.absolute, {bottom: 24, right: 16}, a.z_30]}
          pointerEvents="box-none">
          {skipStage !== 'hidden' && (
            <SkipControl
              stage={skipStage}
              countdown={skipCountdown}
              progress={skipProgress}
              onSkip={onSkip}
            />
          )}
        </View>
      )}
    </View>
  )
}

/**
 * Capsule description. Collapsed to 2 lines (tap to expand) in scroll mode;
 * fully readable — constrained to a scrollable region — once playing/ended.
 */
function CapsuleDescription({
  richText,
  mode,
  reserveRight,
}: {
  richText: RichTextAPI
  mode: CapsuleOverlayMode
  reserveRight: boolean
}) {
  const {t: l} = useLingui()
  const {height: screenHeight} = useSafeAreaFrame()
  const [clamped, setClamped] = useState(false)
  const [expanded, setExpanded] = useState(mode !== 'scroll')
  const [contentHeight, setContentHeight] = useState(0)

  const forceExpanded = mode !== 'scroll'
  const showClamped = !forceExpanded && clamped && !expanded

  const onTextLayout = useCallback(
    (evt: NativeSyntheticEvent<TextLayoutEventData>) => {
      if (forceExpanded || clamped) return
      if (evt.nativeEvent.lines.length > 2) {
        setClamped(true)
      }
    },
    [forceExpanded, clamped],
  )

  const text = (
    <RichText
      value={richText}
      style={[
        a.text_sm,
        a.leading_relaxed,
        {color: 'white'},
        reserveRight && {marginRight: 150},
      ]}
      numberOfLines={showClamped ? 2 : undefined}
      onTextLayout={onTextLayout}
    />
  )

  const container = (children: ReactNode) => (
    <View
      style={[
        a.relative,
        a.mt_xs,
        forceExpanded && {maxHeight: screenHeight * 0.3},
      ]}>
      {children}
    </View>
  )

  if (!forceExpanded && !clamped) {
    return container(text)
  }
  if (!forceExpanded && clamped && !expanded) {
    return container(
      <>
        {text}
        {!showClamped ? null : (
          <Pressable
            accessibilityHint={l`Expands the full capsule description`}
            accessibilityLabel={l`Read more`}
            hitSlop={HITSLOP_20}
            onPress={() => setExpanded(true)}
            style={[a.absolute, a.inset_0]}
          />
        )}
      </>,
    )
  }
  // forceExpanded || expanded — full text, scrollable if it doesn't fit
  return (
    <View style={[a.mt_xs]}>
      <ScrollView
        style={{maxHeight: screenHeight * 0.3}}
        scrollEnabled={contentHeight > screenHeight * 0.3}
        onContentSizeChange={(_w: number, h: number) => setContentHeight(h)}
        nestedScrollEnabled>
        {text}
      </ScrollView>
    </View>
  )
}

/**
 * Ended message: helps the user play the capsule or swipe up for the next
 * one. Auto-scrolls after `hintSeconds` when the user doesn't act.
 */
function EndedMessage({
  onPlay,
  hintSeconds,
  canAutoAdvance,
}: {
  onPlay: () => void
  hintSeconds: number | null
  canAutoAdvance: boolean
}) {
  const {t: l} = useLingui()
  const countdown = canAutoAdvance && hintSeconds != null
  return (
    <View
      style={[
        a.mt_sm,
        {
          backgroundColor: 'rgba(16,16,22,0.92)',
          borderColor: 'rgba(255,255,255,0.08)',
          borderWidth: StyleSheet.hairlineWidth,
          borderRadius: 18,
          padding: 14,
        },
      ]}>
      <View style={[a.flex_row, a.align_center, a.gap_md]}>
        {canAutoAdvance && <SwipeUpLottie size={54} />}
        <View style={[a.flex_1, a.gap_2xs]}>
          <Text style={[a.text_md, a.font_bold, {color: 'white'}]}>
            <Trans>The video has ended</Trans>
          </Text>
          <Text
            style={[
              a.text_sm,
              a.leading_snug,
              {color: 'rgba(255,255,255,0.72)'},
            ]}>
            {canAutoAdvance ? (
              <Trans>Play the capsule, or swipe up for the next one.</Trans>
            ) : (
              <Trans>
                This is the last capsule in the feed — play it, or come back
                later.
              </Trans>
            )}
          </Text>
        </View>
      </View>
      <View style={[a.flex_row, a.align_center, a.gap_sm, {marginTop: 12}]}>
        <Button
          label={l`Play capsule`}
          onPress={onPlay}
          size="small"
          variant="solid"
          color="primary"
          style={[a.flex_1, a.rounded_full]}>
          <ButtonIcon icon={GameController} position="left" />
          <ButtonText style={[a.font_bold]}>
            <Trans>Play</Trans>
          </ButtonText>
        </Button>
        {countdown ? (
          <SkipRing
            progress={Math.max(0, Math.min(1, hintSeconds / 5))}
            size={34}>
            <Text style={[a.text_xs, a.font_bold, {color: 'white'}]}>
              {hintSeconds}
            </Text>
          </SkipRing>
        ) : null}
      </View>
    </View>
  )
}

/** Skip pill shown while playing (countdown → active Skip at 75%). */
function SkipControl({
  stage,
  countdown,
  progress,
  onSkip,
}: {
  stage: CapsuleSkipStage
  countdown: number
  progress: number
  onSkip: () => void
}) {
  const {t: l} = useLingui()
  if (stage === 'active') {
    return (
      <Button
        label={l`Skip`}
        accessibilityHint={l`Skips the video and opens the quiz`}
        onPress={onSkip}
        size="large"
        variant="solid"
        color="secondary_inverted"
        style={[a.rounded_full, {paddingHorizontal: 20}]}>
        <ButtonText style={[a.font_bold]}>
          <Trans>Skip</Trans>
        </ButtonText>
      </Button>
    )
  }
  return (
    <View
      accessibilityLabel={l`Skip in ${countdown} seconds`}
      accessibilityHint={l`Skip becomes available when the countdown ends`}
      style={[
        a.flex_row,
        a.align_center,
        a.gap_xs,
        {
          backgroundColor: 'rgba(0, 0, 0, 0.55)',
          borderRadius: 999,
          paddingLeft: 14,
          paddingRight: 6,
          paddingVertical: 6,
        },
      ]}>
      <Text style={[a.text_sm, a.font_semi_bold, {color: 'white'}]}>
        <Trans>Skip in</Trans>
      </Text>
      <SkipRing progress={progress} size={32}>
        <Text style={[a.text_xs, a.font_bold, {color: 'white'}]}>
          {countdown}
        </Text>
      </SkipRing>
    </View>
  )
}

/**
 * Circular countdown badge with a progress ring. Used for the skip control
 * and the ended-message auto-scroll countdown.
 */
function SkipRing({
  progress = 0,
  size = 34,
  children,
}: {
  progress?: number
  size?: number
  children?: ReactNode
}) {
  const clamped = Math.min(1, Math.max(0, progress))
  const stroke = 3
  const r = (size - stroke) / 2
  const circumference = 2 * Math.PI * r
  const center = size / 2
  const inner = size - 8
  return (
    <View style={{width: size, height: size}}>
      <Svg width={size} height={size} style={[StyleSheet.absoluteFill]}>
        <Circle
          cx={center}
          cy={center}
          r={r}
          stroke="rgba(255, 255, 255, 0.25)"
          strokeWidth={stroke}
          fill="none"
        />
        <Circle
          cx={center}
          cy={center}
          r={r}
          stroke="#FFD400"
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - clamped)}
          transform={`rotate(-90 ${center} ${center})`}
        />
      </Svg>
      <View style={[a.absolute, a.inset_0, a.align_center, a.justify_center]}>
        <View
          style={{
            width: inner,
            height: inner,
            borderRadius: inner / 2,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          {children}
        </View>
      </View>
    </View>
  )
}
